import * as THREE from 'three';
import type { GeoObjectMeshOptions, Materials, RawDataGroup, RawDataObject, RawDataPoint, RawTriangle } from '@visualizer/types';
import { BuildingSource, PreProcessingTypes } from '@visualizer/types';
import {
    createDefaultFloorMaterial,
    createDefaultRoofMaterial,
    createDefaultWallMaterial,
    updateProjectSketchSensorDistances,
} from './utils';
import { GeoObjectBatched } from '@data/geodata/GeoObjectBatched';
import type { ObjectVisual } from '../edit/Interfaces/ObjectVisual';
import { BuildingPickingVisual } from '../edit/Interfaces/BuildingPickingVisual';
import { HighlightVisual } from '../edit/Interfaces/HighlightVisual';
import type { Intersection } from 'three';
import { getOnlyGeometryFromRawData } from '../utils';

// -----------------------------------------------------------------------------
// Common helper types
// -----------------------------------------------------------------------------

type SwisstopoTypes = [PreProcessingTypes.SWISSTOPO_ROOF, PreProcessingTypes.SWISSTOPO_WALL, PreProcessingTypes.SWISSTOPO_FLOOR];

type CustomBuildingsTypes = [
    PreProcessingTypes.CUSTOM_BUILDINGS_ROOF,
    PreProcessingTypes.CUSTOM_BUILDINGS_WALL,
    PreProcessingTypes.CUSTOM_BUILDINGS_FLOOR,
];
type ProjectSketchTypes = [
    PreProcessingTypes.PROJECT_SKETCH_ROOF,
    PreProcessingTypes.PROJECT_SKETCH_WALL,
    PreProcessingTypes.PROJECT_SKETCH_FLOOR,
];

type InstanceRef<T extends PreProcessingTypes> = {
    type: T;
    instanceId: number;
};

// Small helper to avoid duplicating the mapping logic
function buildInstanceMapping<T extends readonly PreProcessingTypes[]>(
    types: T,
    data: RawDataGroup[],
    pushRef: (buildingId: number, ref: InstanceRef<T[number]>) => void
): void {
    for (const type of types) {
        const group = data.find((g) => g.type === type);
        if (!group) continue;

        group.objects.forEach((obj, instanceId) => {
            const triWithId = obj.triangles.find((t) => t.ID != null);
            if (!triWithId || triWithId.ID == null) return;

            const buildingId = triWithId.ID;
            const ref: InstanceRef<T[number]> = { type, instanceId };

            pushRef(buildingId, ref);
        });
    }
}

// ---------------------------------------------------------------------------
// Raw data helpers (geometry -> RawDataObject)
// ---------------------------------------------------------------------------
function geometryToRawDataObject(geom: THREE.BufferGeometry, buildingId: number, buildingSource: BuildingSource): RawDataObject {
    const position = geom.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (!position) {
        return { points: [], triangles: [] };
    }
    //get vertices coordinates
    const points: RawDataPoint[] = [];
    for (let i = 0; i < position.count; i++) {
        points.push({ x: position.getX(i), y: position.getY(i), z: position.getZ(i) });
    }

    // get triangle indices
    const indexAttr = geom.getIndex();
    const indices = indexAttr ? Array.from(indexAttr.array as ArrayLike<number>) : [...Array(position.count).keys()];

    // group every 3 indices into a triangle
    const triangles: RawTriangle[] = [];
    for (let i = 0; i + 2 < indices.length; i += 3) {
        const a = Number(indices[i]);
        const b = Number(indices[i + 1]);
        const c = Number(indices[i + 2]);
        // Swap B/C so round-tripping through getOnlyGeometryFromRawData preserves indices.
        triangles.push({ A: a, B: c, C: b, ID: buildingId, buildingSource });
    }

    return { points, triangles };
}

function buildRawGroupsFromRefs<T extends PreProcessingTypes>(
    refs: Array<InstanceRef<T>>,
    sourceGeoms: Partial<Record<T, THREE.BufferGeometry[]>>,
    mapType: (type: PreProcessingTypes) => PreProcessingTypes,
    targetBuildingId: number,
    targetSource: BuildingSource
): RawDataGroup[] {
    const grouped = new Map<PreProcessingTypes, RawDataObject[]>();

    for (const { type, instanceId } of refs) {
        const geoms = sourceGeoms[type];
        const geom = geoms?.[instanceId];
        if (!geom) continue;

        const rawObj = geometryToRawDataObject(geom, targetBuildingId, targetSource);
        const targetType = mapType(type);
        const list = grouped.get(targetType) ?? [];
        list.push(rawObj);
        grouped.set(targetType, list);
    }

    return Array.from(grouped.entries()).map(([type, objects]) => ({ type, objects }));
}

// -----------------------------------------------------------------------------
// Intersection Helper
// -----------------------------------------------------------------------------
interface BatchedIntersection extends Intersection {
    batchId?: number;
}

function getInstanceIdFromIntersection(intersection: BatchedIntersection): number | null {
    if (typeof intersection.batchId === 'number') {
        return intersection.batchId;
    }
    if (typeof intersection.instanceId === 'number') {
        return intersection.instanceId;
    }
    return null;
}

// -----------------------------------------------------------------------------
// Swisstopo
// -----------------------------------------------------------------------------
type SwisstopoInstanceRef = InstanceRef<SwisstopoTypes[number]>;

export class SwisstopoBatchedMesh extends GeoObjectBatched<SwisstopoTypes> implements ObjectVisual, BuildingPickingVisual, HighlightVisual {
    private readonly buildingIdToInstances = new Map<number, SwisstopoInstanceRef[]>();
    private readonly storedMatrices = new Map<string, THREE.Matrix4>();

    // Highlight state
    private readonly highlightedInstances = new Set<string>();

    constructor({ name, colors, data, mode, thresholdAngle }: Omit<GeoObjectMeshOptions<SwisstopoTypes>, 'types'>) {
        super({
            name,
            colors,
            mode,
            types: [PreProcessingTypes.SWISSTOPO_ROOF, PreProcessingTypes.SWISSTOPO_WALL, PreProcessingTypes.SWISSTOPO_FLOOR],
            thresholdAngle,
            data,
        });

        this.userData.buildingSource = BuildingSource.SWISSTOPO;

        buildInstanceMapping(this.types, data, (buildingId, ref) => {
            const list = this.buildingIdToInstances.get(buildingId) ?? [];
            list.push(ref as SwisstopoInstanceRef);
            this.buildingIdToInstances.set(buildingId, list);
        });

        for (const t of this.types) {
            (this.objects[t].meshes as THREE.BatchedMesh).userData.buildingGeometry = true;
        }

        // Resolve coincident edge overlaps (draw roof last so it stay visible):
        this.objects[PreProcessingTypes.SWISSTOPO_FLOOR].edges.renderOrder = 2;
        this.objects[PreProcessingTypes.SWISSTOPO_WALL].edges.renderOrder = 3;
        this.objects[PreProcessingTypes.SWISSTOPO_ROOF].edges.renderOrder = 4;
    }

    protected override createMaterials(): Materials<SwisstopoTypes> {
        return {
            swisstopoFloor: createDefaultFloorMaterial(),
            swisstopoRoof: createDefaultRoofMaterial(),
            swisstopoWall: createDefaultWallMaterial(),
        };
    }

    protected override shouldShowEdgesInRenderMode(type: SwisstopoTypes[number]): boolean {
        return type !== PreProcessingTypes.SWISSTOPO_ROOF;
    }

    protected override shouldCastShadowsForType(type: SwisstopoTypes[number]): boolean {
        return type !== PreProcessingTypes.SWISSTOPO_FLOOR;
    }

    // ---------------------------------------------------------------------
    // BuildingsVisual implementation
    // ---------------------------------------------------------------------
    hideObject(id: number): void {
        const refs = this.buildingIdToInstances.get(id);
        if (!refs) return;

        const tmp = new THREE.Matrix4();
        const pos = new THREE.Vector3();
        const quat = new THREE.Quaternion();
        const scale = new THREE.Vector3();
        const dirtyTypes = new Set<SwisstopoTypes[number]>();

        for (const { type, instanceId } of refs) {
            const key = this.makeKey(type, instanceId);
            const group = this.objects[type];
            const mesh = group.meshes;

            if (!this.storedMatrices.has(key)) {
                mesh.getMatrixAt(instanceId, tmp);
                this.storedMatrices.set(key, tmp.clone());
            } else {
                tmp.copy(this.storedMatrices.get(key)!);
            }

            tmp.decompose(pos, quat, scale);
            scale.set(0, 0, 0);
            tmp.compose(pos, quat, scale);

            mesh.setMatrixAt(instanceId, tmp);
            dirtyTypes.add(type);
        }
    }

    showObject(id: number): void {
        const refs = this.buildingIdToInstances.get(id);
        if (!refs) return;

        const dirtyTypes = new Set<SwisstopoTypes[number]>();

        for (const { type, instanceId } of refs) {
            const key = this.makeKey(type, instanceId);
            const original = this.storedMatrices.get(key);
            if (!original) continue;

            const group = this.objects[type];
            group.meshes.setMatrixAt(instanceId, original);
            dirtyTypes.add(type);
            this.storedMatrices.delete(key);
        }
    }

    private makeKey(type: SwisstopoTypes[number], instanceId: number): string {
        const typeIndex = this.types.indexOf(type);
        return `${typeIndex}:${instanceId}`;
    }

    // ---------------------------------------------------------------------
    // BuildingPickingVisual implementation
    // ---------------------------------------------------------------------
    getBuildingIdFromIntersection(intersection: THREE.Intersection): number | null {
        const instanceId = getInstanceIdFromIntersection(intersection as BatchedIntersection);
        if (instanceId == null) return null;

        const mesh = intersection.object;

        // find which PreProcessingType this mesh corresponds to
        const type = this.types.find((t) => this.objects[t].meshes === mesh);
        if (!type) return null;

        // linear scan over our existing mapping
        for (const [buildingId, refs] of this.buildingIdToInstances) {
            if (refs.some((ref) => ref.type === type && ref.instanceId === instanceId)) {
                return buildingId;
            }
        }

        return null;
    }

    // ------------------------------------------------------------------
    // Highlight implementation
    // ------------------------------------------------------------------
    highlightObject(id: number): void {
        const refs = this.buildingIdToInstances.get(id);
        if (!refs) return;

        const highlightColor = new THREE.Color(0xff4444); // red
        for (const { type, instanceId } of refs) {
            const key = this.makeKey(type, instanceId);
            const group = this.objects[type];
            const mesh = group.meshes as THREE.BatchedMesh;

            // setColorAt will lazily init the color texture if needed
            mesh.setColorAt(instanceId, highlightColor);
            this.highlightedInstances.add(key);
        }
    }

    clearHighlight(): void {
        if (this.highlightedInstances.size === 0) return;

        const white = new THREE.Color(0xffffff);

        for (const key of this.highlightedInstances) {
            const [typeIndexStr, instanceStr] = key.split(':');
            const typeIndex = Number(typeIndexStr);
            const instanceId = Number(instanceStr);

            if (!Number.isFinite(typeIndex)) continue;
            if (typeIndex < 0 || typeIndex >= this.types.length) continue;

            const type = this.types[typeIndex];
            const group = this.objects[type];
            const mesh = group.meshes as THREE.BatchedMesh;

            mesh.setColorAt(instanceId, white);
        }

        this.highlightedInstances.clear();
    }

    // ------------------------------------------------------------------
    // Raw data extraction (for edit operations)
    // ------------------------------------------------------------------
    getRawGroupsForBuilding(
        buildingId: number,
        targetBuildingId: number,
        targetSource: BuildingSource,
        mapType: (type: PreProcessingTypes) => PreProcessingTypes
    ): RawDataGroup[] {
        const refs = this.buildingIdToInstances.get(buildingId);
        if (!refs || refs.length === 0) return [];

        return buildRawGroupsFromRefs(refs, this.sourceGeoms, mapType, targetBuildingId, targetSource);
    }
}

// -----------------------------------------------------------------------------
// Custom Buildings
// -----------------------------------------------------------------------------
type CustomInstanceRef = InstanceRef<CustomBuildingsTypes[number]>;

export class CustomBuildingsBatchedMesh extends GeoObjectBatched<CustomBuildingsTypes> implements ObjectVisual, BuildingPickingVisual {
    private readonly buildingIdToInstances = new Map<number, CustomInstanceRef[]>();
    private readonly storedMatrices = new Map<string, THREE.Matrix4>();

    // Highlight state
    private readonly highlightedInstances = new Set<string>();

    constructor({ name, colors, data, mode, thresholdAngle }: Omit<GeoObjectMeshOptions<CustomBuildingsTypes>, 'types'>) {
        super({
            name,
            colors,
            mode,
            types: [
                PreProcessingTypes.CUSTOM_BUILDINGS_ROOF,
                PreProcessingTypes.CUSTOM_BUILDINGS_WALL,
                PreProcessingTypes.CUSTOM_BUILDINGS_FLOOR,
            ],
            thresholdAngle,
            data,
        });

        this.userData.buildingSource = BuildingSource.CUSTOM_OBS;

        buildInstanceMapping(this.types, data, (buildingId, ref) => {
            const list = this.buildingIdToInstances.get(buildingId) ?? [];
            list.push(ref as CustomInstanceRef);
            this.buildingIdToInstances.set(buildingId, list);
        });

        for (const t of this.types) {
            (this.objects[t].meshes as THREE.BatchedMesh).userData.buildingGeometry = true;
        }

        // Resolve coincident edge overlaps (draw roof last so it stay visible):
        this.objects[PreProcessingTypes.CUSTOM_BUILDINGS_FLOOR].edges.renderOrder = 2;
        this.objects[PreProcessingTypes.CUSTOM_BUILDINGS_WALL].edges.renderOrder = 3;
        this.objects[PreProcessingTypes.CUSTOM_BUILDINGS_ROOF].edges.renderOrder = 4;
    }

    protected override createMaterials(): Materials<CustomBuildingsTypes> {
        return {
            customBuildingsFloor: createDefaultFloorMaterial(),
            customBuildingsRoof: createDefaultRoofMaterial(),
            customBuildingsWall: createDefaultWallMaterial(),
        };
    }

    protected override shouldShowEdgesInRenderMode(type: CustomBuildingsTypes[number]): boolean {
        return type !== PreProcessingTypes.CUSTOM_BUILDINGS_ROOF;
    }

    protected override shouldCastShadowsForType(type: CustomBuildingsTypes[number]): boolean {
        return type !== PreProcessingTypes.CUSTOM_BUILDINGS_FLOOR;
    }

    // ---------------------------------------------------------------------
    // Add Custom Logic
    // ---------------------------------------------------------------------
    addBuildingFromGroups(buildingId: number, groups: RawDataGroup[]): void {
        for (const type of this.types) {
            const group = groups.find((g) => g.type === type);
            if (!group) continue;

            const mesh = this.objects[type].meshes as THREE.BatchedMesh;

            for (const obj of group.objects) {
                const geom = getOnlyGeometryFromRawData(obj);
                geom.computeVertexNormals();

                // Store for later edge/wireframe rebuild (after-save)
                (this.sourceGeoms[type] ??= []).push(geom);

                // if capacity is exceeded these will throw → you’ll see it immediately
                const geometryId = mesh.addGeometry(geom);
                const instanceId = mesh.addInstance(geometryId);

                mesh.setMatrixAt(instanceId, new THREE.Matrix4());
                mesh.setColorAt(instanceId, new THREE.Color(0xffffff));

                const list = this.buildingIdToInstances.get(buildingId) ?? [];
                list.push({ type, instanceId } as any);
                this.buildingIdToInstances.set(buildingId, list);
            }

            mesh.computeBoundingBox();
            mesh.computeBoundingSphere();
        }
    }

    // ---------------------------------------------------------------------
    // BuildingsVisual implementation
    // ---------------------------------------------------------------------
    hideObject(id: number): void {
        const refs = this.buildingIdToInstances.get(id);
        if (!refs) return;

        const tmp = new THREE.Matrix4();
        const pos = new THREE.Vector3();
        const quat = new THREE.Quaternion();
        const scale = new THREE.Vector3();
        const dirtyTypes = new Set<CustomBuildingsTypes[number]>();

        for (const { type, instanceId } of refs) {
            const key = this.makeKey(type, instanceId);
            const group = this.objects[type];
            const mesh = group.meshes;

            if (!this.storedMatrices.has(key)) {
                mesh.getMatrixAt(instanceId, tmp);
                this.storedMatrices.set(key, tmp.clone());
            } else {
                tmp.copy(this.storedMatrices.get(key)!);
            }

            tmp.decompose(pos, quat, scale);
            scale.set(0, 0, 0);
            tmp.compose(pos, quat, scale);

            mesh.setMatrixAt(instanceId, tmp);
            dirtyTypes.add(type);
        }
    }

    showObject(id: number): void {
        const refs = this.buildingIdToInstances.get(id);
        if (!refs) return;

        const dirtyTypes = new Set<CustomBuildingsTypes[number]>();

        for (const { type, instanceId } of refs) {
            const key = this.makeKey(type, instanceId);
            const original = this.storedMatrices.get(key);
            if (!original) continue;

            const group = this.objects[type];
            group.meshes.setMatrixAt(instanceId, original);
            dirtyTypes.add(type);
            this.storedMatrices.delete(key);
        }
    }

    hasVisibleBuildings(): boolean {
        for (const refs of this.buildingIdToInstances.values()) {
            if (this.hasVisibleRefs(refs)) return true;
        }
        return false;
    }

    private makeKey(type: CustomBuildingsTypes[number], instanceId: number): string {
        const typeIndex = this.types.indexOf(type);
        return `${typeIndex}:${instanceId}`;
    }

    private hasVisibleRefs(refs: CustomInstanceRef[]): boolean {
        for (const { type, instanceId } of refs) {
            const key = this.makeKey(type, instanceId);
            if (!this.storedMatrices.has(key)) return true;
        }
        return false;
    }

    // ---------------------------------------------------------------------
    // BuildingPickingVisual implementation
    // ---------------------------------------------------------------------
    getBuildingIdFromIntersection(intersection: THREE.Intersection): number | null {
        const instanceId = getInstanceIdFromIntersection(intersection as BatchedIntersection);
        if (instanceId == null) return null;

        const mesh = intersection.object;
        const type = this.types.find((t) => this.objects[t].meshes === mesh);
        if (!type) return null;

        for (const [buildingId, refs] of this.buildingIdToInstances) {
            if (refs.some((ref) => ref.type === type && ref.instanceId === instanceId)) {
                return buildingId;
            }
        }

        return null;
    }

    // ---------------------------------------------------------------------
    // HighlightVisual implementation
    // ---------------------------------------------------------------------
    highlightObject(id: number): void {
        const refs = this.buildingIdToInstances.get(id);
        if (!refs) return;

        const highlightColor = new THREE.Color(0xff4444);

        for (const { type, instanceId } of refs) {
            const key = this.makeKey(type, instanceId);
            const group = this.objects[type];
            const mesh = group.meshes as THREE.BatchedMesh;

            mesh.setColorAt(instanceId, highlightColor);
            this.highlightedInstances.add(key);
        }
    }

    clearHighlight(): void {
        if (this.highlightedInstances.size === 0) return;

        const white = new THREE.Color(0xffffff);

        for (const key of this.highlightedInstances) {
            const [typeIndexStr, instanceStr] = key.split(':');
            const typeIndex = Number(typeIndexStr);
            const instanceId = Number(instanceStr);

            if (!Number.isFinite(typeIndex)) continue;
            if (typeIndex < 0 || typeIndex >= this.types.length) continue;

            const type = this.types[typeIndex];
            const group = this.objects[type];
            const mesh = group.meshes as THREE.BatchedMesh;

            mesh.setColorAt(instanceId, white);
        }

        this.highlightedInstances.clear();
    }

    // ------------------------------------------------------------------
    // Raw data extraction (for edit operations)
    // ------------------------------------------------------------------
    getRawGroupsForBuilding(
        buildingId: number,
        targetBuildingId: number,
        targetSource: BuildingSource,
        mapType: (type: PreProcessingTypes) => PreProcessingTypes
    ): RawDataGroup[] {
        const refs = this.buildingIdToInstances.get(buildingId);
        if (!refs || refs.length === 0) return [];

        return buildRawGroupsFromRefs(refs, this.sourceGeoms, mapType, targetBuildingId, targetSource);
    }
}

// -----------------------------------------------------------------------------
// Project Sketch
// -----------------------------------------------------------------------------
type SketchInstanceRef = InstanceRef<ProjectSketchTypes[number]>;

export class ProjectSketchBatchedMesh extends GeoObjectBatched<ProjectSketchTypes> implements ObjectVisual, BuildingPickingVisual {
    private readonly buildingIdToInstances = new Map<number, SketchInstanceRef[]>();
    private readonly storedMatrices = new Map<string, THREE.Matrix4>();

    private readonly highlightedInstances = new Set<string>();
    private readonly rawGroups: RawDataGroup[];

    constructor({ name, colors, data, mode, thresholdAngle }: Omit<GeoObjectMeshOptions<ProjectSketchTypes>, 'types'>) {
        super({
            name,
            colors,
            mode,
            types: [
                PreProcessingTypes.PROJECT_SKETCH_ROOF,
                PreProcessingTypes.PROJECT_SKETCH_WALL,
                PreProcessingTypes.PROJECT_SKETCH_FLOOR,
            ],
            thresholdAngle,
            data,
        });
        this.rawGroups = ProjectSketchBatchedMesh.buildRawGroups(data);
        updateProjectSketchSensorDistances(this.rawGroups);

        this.userData.buildingSource = BuildingSource.SKETCH;

        buildInstanceMapping(this.types, data, (buildingId, ref) => {
            const list = this.buildingIdToInstances.get(buildingId) ?? [];
            list.push(ref as SketchInstanceRef);
            this.buildingIdToInstances.set(buildingId, list);
        });

        for (const t of this.types) {
            (this.objects[t].meshes as THREE.BatchedMesh).userData.buildingGeometry = true;
        }

        // Resolve coincident edge overlaps (draw roof last so it stay visible):
        this.objects[PreProcessingTypes.PROJECT_SKETCH_FLOOR].edges.renderOrder = 2;
        this.objects[PreProcessingTypes.PROJECT_SKETCH_WALL].edges.renderOrder = 3;
        this.objects[PreProcessingTypes.PROJECT_SKETCH_ROOF].edges.renderOrder = 4;
    }

    private static buildRawGroups(data: RawDataGroup[]): RawDataGroup[] {
        const types = [
            PreProcessingTypes.PROJECT_SKETCH_ROOF,
            PreProcessingTypes.PROJECT_SKETCH_WALL,
            PreProcessingTypes.PROJECT_SKETCH_FLOOR,
        ] as const;

        const groups: RawDataGroup[] = types.map((type) => ({ type, objects: [] }));

        for (const group of data) {
            const target = groups.find((g) => g.type === group.type);
            if (!target) continue;
            target.objects.push(...(group.objects ?? []));
        }

        return groups;
    }

    protected override createMaterials(): Materials<ProjectSketchTypes> {
        return {
            projectSketchFloor: createDefaultFloorMaterial(),
            projectSketchRoof: createDefaultRoofMaterial(),
            projectSketchWall: createDefaultWallMaterial(),
        };
    }

    protected override shouldShowEdgesInRenderMode(type: ProjectSketchTypes[number]): boolean {
        return type !== PreProcessingTypes.PROJECT_SKETCH_ROOF;
    }

    protected override shouldCastShadowsForType(type: ProjectSketchTypes[number]): boolean {
        return type !== PreProcessingTypes.PROJECT_SKETCH_FLOOR;
    }

    // ---------------------------------------------------------------------
    // Add Project Sketch Logic
    // ---------------------------------------------------------------------
    addBuildingFromGroups(buildingId: number, groups: RawDataGroup[]): void {
        for (const type of this.types) {
            const group = groups.find((g) => g.type === type);
            if (!group) continue;

            const mesh = this.objects[type].meshes as THREE.BatchedMesh;

            for (const obj of group.objects) {
                const geom = getOnlyGeometryFromRawData(obj);
                geom.computeVertexNormals();

                (this.sourceGeoms[type] ??= []).push(geom);

                const geometryId = mesh.addGeometry(geom);
                const instanceId = mesh.addInstance(geometryId);

                mesh.setMatrixAt(instanceId, new THREE.Matrix4());
                mesh.setColorAt(instanceId, new THREE.Color(0xffffff));

                const list = this.buildingIdToInstances.get(buildingId) ?? [];
                list.push({ type, instanceId } as any);
                this.buildingIdToInstances.set(buildingId, list);
            }

            mesh.computeBoundingBox();
            mesh.computeBoundingSphere();

            const rawGroup = this.rawGroups.find((g) => g.type === type);
            if (rawGroup) {
                rawGroup.objects.push(...(group.objects ?? []));
            }
        }

        updateProjectSketchSensorDistances(this.rawGroups);
    }

    // ---------------------------------------------------------------------
    // BuildingsVisual implementation
    // ---------------------------------------------------------------------
    hideObject(id: number): void {
        const refs = this.buildingIdToInstances.get(id);
        if (!refs) return;

        const tmp = new THREE.Matrix4();
        const pos = new THREE.Vector3();
        const quat = new THREE.Quaternion();
        const scale = new THREE.Vector3();
        const dirtyTypes = new Set<ProjectSketchTypes[number]>();

        for (const { type, instanceId } of refs) {
            const key = this.makeKey(type, instanceId);
            const group = this.objects[type];
            const mesh = group.meshes;

            if (!this.storedMatrices.has(key)) {
                mesh.getMatrixAt(instanceId, tmp);
                this.storedMatrices.set(key, tmp.clone());
            } else {
                tmp.copy(this.storedMatrices.get(key)!);
            }

            tmp.decompose(pos, quat, scale);
            scale.set(0, 0, 0);
            tmp.compose(pos, quat, scale);

            mesh.setMatrixAt(instanceId, tmp);
            dirtyTypes.add(type);
        }
    }

    showObject(id: number): void {
        const refs = this.buildingIdToInstances.get(id);
        if (!refs) return;

        const dirtyTypes = new Set<ProjectSketchTypes[number]>();

        for (const { type, instanceId } of refs) {
            const key = this.makeKey(type, instanceId);
            const original = this.storedMatrices.get(key);
            if (!original) continue;

            const group = this.objects[type];
            group.meshes.setMatrixAt(instanceId, original);
            dirtyTypes.add(type);
            this.storedMatrices.delete(key);
        }
    }

    hasVisibleBuildings(): boolean {
        for (const refs of this.buildingIdToInstances.values()) {
            if (this.hasVisibleRefs(refs)) return true;
        }
        return false;
    }

    private makeKey(type: ProjectSketchTypes[number], instanceId: number): string {
        const typeIndex = this.types.indexOf(type);
        return `${typeIndex}:${instanceId}`;
    }

    private hasVisibleRefs(refs: SketchInstanceRef[]): boolean {
        for (const { type, instanceId } of refs) {
            const key = this.makeKey(type, instanceId);
            if (!this.storedMatrices.has(key)) return true;
        }
        return false;
    }

    // ---------------------------------------------------------------------
    // BuildingPickingVisual implementation
    // ---------------------------------------------------------------------
    getBuildingIdFromIntersection(intersection: THREE.Intersection): number | null {
        const instanceId = getInstanceIdFromIntersection(intersection as BatchedIntersection);
        if (instanceId == null) return null;

        const mesh = intersection.object;
        const type = this.types.find((t) => this.objects[t].meshes === mesh);
        if (!type) return null;

        for (const [buildingId, refs] of this.buildingIdToInstances) {
            if (refs.some((ref) => ref.type === type && ref.instanceId === instanceId)) {
                return buildingId;
            }
        }

        return null;
    }

    // ---------------------------------------------------------------------
    // HighlightVisual implementation
    // ---------------------------------------------------------------------
    highlightObject(id: number): void {
        const refs = this.buildingIdToInstances.get(id);
        if (!refs) return;

        const highlightColor = new THREE.Color(0xff4444);

        for (const { type, instanceId } of refs) {
            const key = this.makeKey(type, instanceId);
            const group = this.objects[type];
            const mesh = group.meshes as THREE.BatchedMesh;

            mesh.setColorAt(instanceId, highlightColor);
            this.highlightedInstances.add(key);
        }
    }

    clearHighlight(): void {
        if (this.highlightedInstances.size === 0) return;

        const white = new THREE.Color(0xffffff);

        for (const key of this.highlightedInstances) {
            const [typeIndexStr, instanceStr] = key.split(':');
            const typeIndex = Number(typeIndexStr);
            const instanceId = Number(instanceStr);

            if (!Number.isFinite(typeIndex)) continue;
            if (typeIndex < 0 || typeIndex >= this.types.length) continue;

            const type = this.types[typeIndex];
            const group = this.objects[type];
            const mesh = group.meshes as THREE.BatchedMesh;

            mesh.setColorAt(instanceId, white);
        }

        this.highlightedInstances.clear();
    }

    // ------------------------------------------------------------------
    // Raw data extraction (for edit operations)
    // ------------------------------------------------------------------
    getRawGroupsForBuilding(
        buildingId: number,
        targetBuildingId: number,
        targetSource: BuildingSource,
        mapType: (type: PreProcessingTypes) => PreProcessingTypes
    ): RawDataGroup[] {
        const refs = this.buildingIdToInstances.get(buildingId);
        if (!refs || refs.length === 0) return [];

        return buildRawGroupsFromRefs(refs, this.sourceGeoms, mapType, targetBuildingId, targetSource);
    }
}
